/**
 * Pinned, reviewed public-health snapshots verified on 2026-10-03.
 * These values are not live clinical data and must not be used for diagnosis or care decisions.
 */

export type OpenHealthStatColorTone =
  | "blue"
  | "teal"
  | "violet"
  | "coral"
  | "green"
  | "amber";

export interface OpenHealthStat {
  id: string;
  displayValue: string;
  label: string;
  yearScope: string;
  context: string;
  caveat: string;
  colorTone: OpenHealthStatColorTone;
  sourceLabel: string;
  sourceUrl: string;
  apiUrl?: string;
}

export const openHealthStats: readonly OpenHealthStat[] = [
  {
    id: "global-life-expectancy",
    displayValue: "73.5 years",
    label: "Global life expectancy",
    yearScope: "2024 · Worldwide",
    context:
      "The World Bank world aggregate was 73.4818 years at birth in 2024.",
    caveat:
      "This period estimate applies current mortality rates; it is not a prediction of an individual's lifespan and masks major differences between populations.",
    colorTone: "blue",
    sourceLabel: "World Bank Open Data",
    sourceUrl:
      "https://data.worldbank.org/indicator/SP.DYN.LE00.IN?locations=1W",
    apiUrl:
      "https://api.worldbank.org/v2/country/WLD/indicator/SP.DYN.LE00.IN?format=json",
  },
  {
    id: "under-five-mortality",
    displayValue: "37.4 per 1,000",
    label: "Under-five mortality",
    yearScope: "2024 · Worldwide live births",
    context:
      "An estimated 37.4 children per 1,000 live births died before reaching age five.",
    caveat:
      "This is a modelled probability rather than a simple count of registered deaths, and global aggregation conceals wide geographic inequality.",
    colorTone: "teal",
    sourceLabel: "World Bank / UN IGME",
    sourceUrl:
      "https://data.worldbank.org/indicator/SH.DYN.MORT?locations=1W",
    apiUrl:
      "https://api.worldbank.org/v2/country/WLD/indicator/SH.DYN.MORT?format=json",
  },
  {
    id: "dtp3-vaccination-coverage",
    displayValue: "84.8%",
    label: "Three-dose DTP coverage",
    yearScope: "2024 · Children aged 12–23 months worldwide",
    context:
      "WHO and UNICEF estimates indicate that 84.78% of children in this age group received three DTP-containing vaccine doses.",
    caveat:
      "Administrative and survey estimates can differ; the global average masks zero-dose children and local coverage gaps and does not guarantee individual immunity.",
    colorTone: "violet",
    sourceLabel: "World Bank, sourced from WHO/UNICEF",
    sourceUrl:
      "https://data.worldbank.org/indicator/SH.IMM.IDPT?locations=1W",
    apiUrl:
      "https://api.worldbank.org/v2/country/WLD/indicator/SH.IMM.IDPT?format=json",
  },
  {
    id: "maternal-mortality-ratio",
    displayValue: "197 per 100,000",
    label: "Maternal mortality ratio",
    yearScope: "2023 · 195 countries and territories",
    context:
      "The inter-agency point estimate was 197 maternal deaths per 100,000 live births, corresponding to about 260,000 deaths.",
    caveat:
      "This is a modelled ratio with an 80% uncertainty interval of 174–234, not a direct count or an individual risk estimate.",
    colorTone: "coral",
    sourceLabel: "WHO and UN Maternal Mortality Estimation Group",
    sourceUrl: "https://www.who.int/publications/i/item/9789240108462",
    apiUrl:
      "https://api.worldbank.org/v2/country/WLD/indicator/SH.STA.MMRT?format=json",
  },
  {
    id: "uhc-service-coverage",
    displayValue: "71 / 100",
    label: "Essential health-service coverage",
    yearScope: "2023 · Worldwide",
    context:
      "The global Universal Health Coverage Service Coverage Index reached 71 points.",
    caveat:
      "This is a unitless composite of tracer services—not the percentage of people fully covered or a measure of care quality. Its definition was revised in 2025.",
    colorTone: "green",
    sourceLabel: "WHO–World Bank UHC Global Monitoring Report 2025",
    sourceUrl:
      "https://www.worldbank.org/en/topic/universalhealthcoverage/publication/2025-global-monitoring-report-gmr",
  },
  {
    id: "health-spending-financial-hardship",
    displayValue: "26%",
    label: "Financial hardship from health spending",
    yearScope: "2022 · Global population",
    context:
      "About 2.1 billion people experienced financial hardship linked to out-of-pocket health spending.",
    caveat:
      "The estimate uses household surveys and modelling under the revised 2025 definition and may miss people who avoid needed care because they cannot afford it.",
    colorTone: "amber",
    sourceLabel: "WHO–World Bank UHC Global Monitoring Report 2025",
    sourceUrl:
      "https://www.who.int/teams/health-financing-and-economics/global-monitoring-report/2025",
  },
] as const;
